import { eUserRole } from '../../../../providers/UserProvider/User.enums.ts';
import { tAdvanceSearchLead } from '../../../AdvanceSearch/AdvanceSearchModal.types.ts';
import { tLeadUsers } from '../../Chat.types.ts';

type tAssignedUser = tAdvanceSearchLead['assignedUser'];

/**
 * RET-1208 — assignment-control decision matrix.
 *
 * | role                        | lead state            | UI                       |
 * | --------------------------- | --------------------- | ------------------------ |
 * | ADMIN / OWNER / VIEWER      | any                   | Dropdown (R7 unchanged)  |
 * | SELLER                      | unassigned            | Dropdown w/ "Asignarme"  |
 * | SELLER                      | self-owned            | Dropdown w/ "Sin asignar"|
 * | SELLER                      | owned by another      | Read-only "Asignado a:"  |
 */
export function canEditAssignment(
  currentUserRole: eUserRole | undefined,
  currentUserId: string | undefined,
  assignedUser: tAssignedUser,
): boolean {
  return (
    currentUserRole !== eUserRole.SELLER
    || !assignedUser?.authID
    || assignedUser.authID === currentUserId
  );
}

/**
 * Whether the lead currently has an owner. Treats empty-string `authID`
 * the same as `undefined` (defensive — backend has been observed to emit
 * empty strings for cleared assignments).
 */
export function isLeadAssigned(assignedUser: tAssignedUser): boolean {
  return Boolean(assignedUser?.authID);
}

/**
 * The "Asignarme este lead" label is SELLER-specific by design (R7).
 * An ADMIN viewing an unassigned lead must see their own plain name,
 * never the SELLER-framed self-claim label.
 */
export function shouldShowAssignToMe(
  user: tLeadUsers,
  currentUserId: string | undefined,
  currentUserRole: eUserRole | undefined,
  assignedUser: tAssignedUser,
): boolean {
  return (
    currentUserRole === eUserRole.SELLER
    && user.auth_id === currentUserId
    && !isLeadAssigned(assignedUser)
  );
}

/**
 * Resolve the menu rows shown to the user: every team member except
 * the current owner, in the same order as `leadUsers`.
 */
export function getAssignableMenuUsers(
  leadUsers: tLeadUsers[],
  assignedUser: tAssignedUser,
): tLeadUsers[] {
  if (!isLeadAssigned(assignedUser)) return leadUsers;
  return leadUsers.filter((user) => user.auth_id !== assignedUser?.authID);
}

/**
 * Resolve the assignee's display name with a multi-stage fallback:
 *   1. `assignedUser.name` from the lead payload (always carried with authID
 *      in the production type, and in the optimistic-update payload post-fix).
 *   2. `leadUsers.find(...)` — covers websocket-arrived shapes where name was
 *      not carried, and defensive for shape drift.
 *   3. `null` — caller should suppress the label (read-only branch) or fall
 *      back to a generic translation.
 *
 * Returns `null` (not empty string) so the caller's `??` chain can distinguish
 * "no name available" from "empty string is the right answer" — avoids the
 * `"Asignado a: "` (trailing colon) rendering that the empty-string fallback
 * produces.
 */
export function resolveAssigneeName(
  assignedUser: tAssignedUser,
  leadUsers: tLeadUsers[],
): string | null {
  if (!assignedUser?.authID) return null;
  if (assignedUser.name) return assignedUser.name;
  const fromTeam = leadUsers.find((user) => user.auth_id === assignedUser.authID);
  return fromTeam?.name ?? null;
}
